/**
 * Cut, Copy and Paste for menus (the Outliner's, the viewport's, Edit), through
 * the async clipboard API behind EntityClipboard. The keyboard goes through
 * clipboard events instead (clipboardEvents.ts); both build the same edits.
 */
import { useMemo } from "react";
import type { CommandOrigin } from "../../commands/types";
import type { EntityId } from "../../world/entity";
import { runEdit, useEditor } from "../EditorContext";
import { copiedText, cutEdit, parseClipboard, pasteEdit } from "../create/entityCopies";
import type { SelectionState } from "../selection/Selection";

export interface EntityClipboardActions {
  /** Whether Copy (and Cut) have something to take: not just the level root. */
  canCopy(ids: readonly EntityId[]): boolean;
  copy(ids: readonly EntityId[]): void;
  /** Copies what `acting` selects, then deletes it, as one undo step. */
  cut(acting: SelectionState): void;
  /** Pastes after the active entity (or under the level root), selected. */
  paste(): Promise<void>;
}

export function useEntityClipboard(origin: CommandOrigin): EntityClipboardActions {
  const { clipboard, components, world, selection, dispatch, dispatchAll } = useEditor();

  return useMemo(() => {
    const textOf = (ids: readonly EntityId[]) =>
      copiedText(world.getSnapshot().entities, components, ids);
    const copy = (ids: readonly EntityId[]) => {
      const text = textOf(ids);
      if (text !== null) void clipboard.write(text);
      return text !== null;
    };

    return {
      canCopy: (ids) => textOf(ids) !== null,
      copy: (ids) => {
        copy(ids);
      },
      cut: (acting) => {
        if (!copy(acting.ids)) return;
        const edit = cutEdit(world.getSnapshot().entities, acting, acting.ids, origin);
        runEdit({ dispatch, dispatchAll }, edit);
      },
      paste: async () => {
        const copies = parseClipboard((await clipboard.read()) ?? "");
        if (!copies) return;
        const { active } = selection.getSnapshot();
        const edit = pasteEdit(copies, world.getSnapshot(), components, active, origin);
        runEdit({ dispatch, dispatchAll }, edit);
      },
    };
  }, [clipboard, components, world, selection, dispatch, dispatchAll, origin]);
}
